import type {
  NotionPropertyMapping,
  NotionReadableType,
  NotionStepConfig,
  NotionTriggerBlock,
  NotionWritableType,
} from '@/types/custom-playbooks';

/**
 * Notion's own play nodes in the builder: the pasted-link parser, the
 * property types and their words, and each node's one-line summary. The
 * server's twin is cleanse-api `utils/play_notion.py`.
 */

export const WRITABLE_TYPES: { value: NotionWritableType; label: string }[] = [
  { value: 'title', label: 'Title' },
  { value: 'rich_text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'date', label: 'Date' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'phone_number', label: 'Phone' },
  { value: 'checkbox', label: 'Checkbox' },
];

export const MATCHABLE_TYPES: NotionWritableType[] = [
  'title',
  'rich_text',
  'number',
  'select',
  'url',
  'email',
  'phone_number',
];

export const READABLE_TYPES: { value: NotionReadableType; label: string }[] = [
  ...WRITABLE_TYPES,
  { value: 'status', label: 'Status' },
  { value: 'unique_id', label: 'ID' },
  { value: 'created_time', label: 'Created time' },
  { value: 'last_edited_time', label: 'Last edited time' },
  { value: 'formula', label: 'Formula' },
];

/** Whether a trigger reads this property type (the server's READABLE_TYPES). */
export function isReadableType(type: unknown): type is NotionReadableType {
  return READABLE_TYPES.some((t) => t.value === type);
}

const HEX32 = /^[0-9a-f]{32}$/i;
const DASHED =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ID_AT_END =
  /([0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12})$/i;
const NOTION_HOST = /^(?:[a-z0-9-]+\.)*(?:notion\.so|notion\.site)$/i;

function dashed(hex: string): string {
  const h = hex.toLowerCase();
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * The database id a pasted Notion link or id names (dashed, lowercase), or
 * null. Mirrors the server's `database_id_of`.
 */
export function notionDatabaseId(
  ref: string | null | undefined
): string | null {
  const text = (ref ?? '').trim();
  if (!text || text.length > 2000) return null;
  if (DASHED.test(text)) return text.toLowerCase();
  if (HEX32.test(text)) return dashed(text);
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(url.protocol) || !NOTION_HOST.test(url.hostname))
    return null;
  const last = url.pathname.replace(/\/+$/, '').split('/').pop() ?? '';
  const found = ID_AT_END.exec(last);
  return found ? dashed(found[1].replace(/-/g, '')) : null;
}

/** A property's trigger key: its name in snake case, unique among `taken`. */
export function propertyKey(name: string, taken: readonly string[]): string {
  const reserved = ['page_id', 'page_url', 'created_time', 'last_edited_time'];
  let base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 36);
  if (!/^[a-z]/.test(base)) base = `p_${base}`.slice(0, 36);
  if (base === 'p_') base = 'property';
  let key = base;
  let n = 2;
  while (taken.includes(key) || reserved.includes(key)) key = `${base}_${n++}`;
  return key;
}

function databaseWords(
  name: string | undefined,
  id: string | null
): string | null {
  if (name?.trim()) return name.trim();
  return id ? 'a Notion database' : null;
}

/** The card's line for a Notion step. */
export function notionStepSummary(
  type: 'notion_add_page' | 'notion_update_page',
  config: Partial<NotionStepConfig>
): string {
  const name = databaseWords(
    config.databaseName,
    notionDatabaseId(config.databaseId) ?? notionDatabaseId(config.databaseUrl)
  );
  if (!name) return 'Paste a database link';
  const count = (config.properties ?? []).length;
  const props = count ? `, ${count} propert${count === 1 ? 'y' : 'ies'}` : '';
  if (type === 'notion_update_page') {
    const by =
      config.match?.by === 'page_id'
        ? ' by page ID'
        : config.match?.property
          ? ` by ${config.match.property}`
          : '';
    return `Update in ${name}${by}${props}`;
  }
  return `Add to ${name}${props}`;
}

/** The trigger card's line. */
export function notionTriggerSummary(
  kind: string | undefined,
  block: Partial<NotionTriggerBlock> | undefined
): string {
  const name = databaseWords(
    block?.databaseName,
    notionDatabaseId(block?.databaseId)
  );
  if (!name) return 'Paste a database link';
  return `Page ${kind === 'notion_page_updated' ? 'updated' : 'added'} in ${name}`;
}

export function defaultNotionStepConfig(
  type: 'notion_add_page' | 'notion_update_page'
): NotionStepConfig {
  return type === 'notion_update_page'
    ? {
        properties: [],
        match: { by: 'property', type: 'email', sourceField: 'primary_email' },
        whenNotFound: 'skip',
      }
    : { properties: [] };
}

/** A new mapping row: a record field unless the user types a value. */
export function blankMapping(): NotionPropertyMapping {
  return { name: '', type: 'rich_text', sourceField: '' };
}

/** Why a Notion trigger can't run yet (the server's twin warns the same). */
export function notionTriggerIssue(
  block: Partial<NotionTriggerBlock> | undefined
): string | null {
  if (!notionDatabaseId(block?.databaseId))
    return 'Paste a Notion database link.';
  const properties = block?.properties ?? [];
  if (properties.some((p) => !p.name?.trim() || !p.key))
    return 'Name each property.';
  const unreadable = properties.find((p) => !isReadableType(p.type));
  if (unreadable)
    return `${unreadable.name.trim()} can’t be read by a trigger.`;
  const keys = properties.map((p) => p.key);
  if (new Set(keys).size !== keys.length) return 'Map each property once.';
  const identity = block?.identity ?? {};
  const named = (key: keyof NotionTriggerBlock['identity']) =>
    !!identity[key] && keys.includes(identity[key] as string);
  if (block?.recordKind === 'company')
    return named('companyDomain') ? null : 'Map a domain property.';
  return named('email') || named('linkedinUrl')
    ? null
    : 'Map an email or LinkedIn property.';
}

function sourceIssue(item: {
  sourceField?: string;
  value?: unknown;
}): string | null {
  const field = item.sourceField;
  const hasValue = item.value !== undefined && item.value !== null;
  if (field !== undefined && hasValue) return 'Pick a record field or a value.';
  if (field !== undefined) return field.trim() ? null : 'Pick a record field.';
  return hasValue ? null : 'Pick a record field or a value.';
}

/** Why a Notion step can't run yet. */
export function notionStepIssue(
  type: 'notion_add_page' | 'notion_update_page',
  config: Partial<NotionStepConfig>
): string | null {
  if (
    !notionDatabaseId(config.databaseId) &&
    !notionDatabaseId(config.databaseUrl)
  )
    return 'Paste a Notion database link.';
  const properties: NotionPropertyMapping[] = config.properties ?? [];
  if (!properties.length) return 'Map at least one property.';
  if (properties.some((p) => !p.name?.trim())) return 'Name each property.';
  const names = properties.map((p) => p.name.trim().toLowerCase());
  if (new Set(names).size !== names.length) return 'Map each property once.';
  for (const p of properties) {
    const issue = sourceIssue(p);
    if (issue) return issue;
  }
  if (type === 'notion_update_page') {
    const match = config.match;
    if (!match) return 'Pick how to find the page.';
    if (match.by === 'property') {
      if (!match.property?.trim()) return 'Pick the property to match on.';
      if (!match.type || !MATCHABLE_TYPES.includes(match.type))
        return 'Pick the property’s type.';
    }
    const issue = sourceIssue(match);
    if (issue) return issue;
  }
  return null;
}
